import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLang } from "@/i18n/LanguageProvider";
import { ArrowUpRight } from "lucide-react";
import logo from "@/assets/dropycash-logo-trimmed.png";
import { trackSignupCtaClick } from "@/lib/analytics";

export function PublicFooter() {
  const { t } = useTranslation();
  const { lp, lang } = useLang();
  const year = new Date().getFullYear();

  const cta = lang === "fr"
    ? {
        title: "Prêt à lancer votre activité de dropshipping au Maroc ?",
        subtitle: "Créez votre compte gratuitement et commencez à vendre avec le paiement à la livraison.",
        button: "Commencer gratuitement",
        rights: "Tous droits réservés.",
        madeIn: "Conçu au Maroc",
        partners: "Partenaires",
        rss: "Flux RSS",
        showcase: "Témoignages",
      }
    : lang === "ar"
    ? {
        title: "جاهز لبدء مشروع الدروبشيبينغ في المغرب؟",
        subtitle: "أنشئ حسابك مجاناً وابدأ البيع بنظام الدفع عند الاستلام.",
        button: "ابدأ مجاناً",
        rights: "جميع الحقوق محفوظة.",
        madeIn: "صُمّم في المغرب",
        partners: "الشركاء",
        rss: "خلاصة RSS",
        showcase: "قصص النجاح",
      }
    : {
        title: "Ready to launch your dropshipping business in Morocco?",
        subtitle: "Create your free account and start selling with cash on delivery.",
        button: "Get started free",
        rights: "All rights reserved.",
        madeIn: "Built in Morocco",
        partners: "Partners",
        rss: "RSS feed",
        showcase: "Success stories",
      };

  const columns = [
    {
      title: t("footer.company"),
      links: [
        { label: t("footer.mission"), to: "/why-choose-us" },
        { label: t("footer.about_us"), to: "/why-choose-us" },
        { label: t("footer.partners", cta.partners), to: "/partners" },
        { label: t("footer.contact"), to: "/support" },
      ],
    },
    {
      title: t("footer.resources"),
      links: [
        { label: t("footer.how_it_works"), to: "/about" },
        { label: t("footer.pricing"), to: "/pricing" },
        { label: t("footer.blog", "Blog"), to: "/blog" },
        { label: t("footer.rss", cta.rss), to: "/rss.xml", external: true },
        { label: t("footer.showcase", cta.showcase), to: "/showcase" },
        { label: t("footer.faq"), to: "/faq" },
      ],
    },
    {
      title: t("footer.legal"),
      links: [
        { label: t("footer.terms"), to: "/terms" },
        { label: t("footer.privacy"), to: "/privacy" },
      ],
    },
  ];

  return (
    <footer className="relative mt-20 w-full overflow-hidden rounded-t-[2rem] bg-[#0b0820] text-white md:rounded-t-[3rem]">
      <div className="pointer-events-none absolute -left-20 -top-20 h-72 w-72 rounded-full bg-primary/30 blur-[120px]" />
      <div className="pointer-events-none absolute -bottom-32 -right-10 h-80 w-80 rounded-full bg-secondary/20 blur-[140px]" />

      <div className="relative mx-auto max-w-7xl px-6 pt-12 md:px-12 md:pt-20">
        <div className="mb-12 flex flex-col items-start justify-between gap-6 rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur-xl md:mb-16 md:flex-row md:items-center md:p-8">
          <div>
            <h2 className="font-headline text-2xl font-extrabold tracking-tight md:text-3xl">
              {cta.title}
            </h2>
            <p className="mt-1 text-sm text-white/80 md:text-base">
              {cta.subtitle}
            </p>
          </div>
          <Link
            to={lp("/signup")}
            onClick={() => trackSignupCtaClick("footer_cta")}
            className="group inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-bold text-[#0b0820] transition-all hover:scale-105"
          >
            {cta.button}
            <ArrowUpRight className="h-4 w-4 transition-transform group-hover:rotate-45" aria-hidden="true" />
          </Link>
        </div>

        <div className="grid grid-cols-2 items-start gap-8 pb-10 md:grid-cols-5 md:gap-12 md:pb-16">
          <div className="col-span-2 space-y-5 md:col-span-2">
            <div className="inline-flex items-center rounded-xl bg-white px-3 py-1.5">
              <img
                src={logo}
                alt="DropyCash"
                width={400}
                height={274}
                className="h-7 w-auto"
                loading="lazy"
                decoding="async"
              />
            </div>
            <p className="max-w-xs text-sm leading-relaxed text-white/80">
              {t("footer.tagline")}
            </p>
          </div>

          {columns.map((col) => (
            <div key={col.title}>
              <h3 className="mb-4 text-xs font-bold uppercase tracking-widest text-white/80">
                {col.title}
              </h3>
              <ul className="space-y-3">
                {col.links.map((l) => (
                  <li key={l.label}>
                    {"external" in l && l.external ? (
                      <a
                        href={l.to}
                        className="inline-block text-sm text-white/90 transition-colors hover:text-white"
                      >
                        {l.label}
                      </a>
                    ) : (
                      <Link
                        to={lp(l.to)}
                        className="inline-block text-sm text-white/90 transition-colors hover:text-white"
                      >
                        {l.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="flex flex-col items-start justify-between gap-3 border-t border-white/10 py-6 text-xs text-white/80 md:flex-row md:items-center md:py-8">
          <p>© {year} DropyCash. {t("footer.rights", cta.rights)}</p>
          <p className="flex items-center gap-2">
            <span className="inline-block h-2 w-2 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.8)]" aria-hidden="true" />
            {t("footer.made_in", cta.madeIn)}
          </p>
        </div>
      </div>
    </footer>
  );
}
